import { grantHasPermission } from "../../../../shared/rbac/roles";
import type { SpaceMode } from "../../../../shared/schema";
import { spaceMemberUserIds } from "../../../lib/countSpaceMembers";

export type SpacesTab = "all" | "member" | "not-member" | "personal";
export type SpaceSortColumn =
  | "name"
  | "members"
  | "workflows"
  | "chats"
  | "created";
export type SortDirection = "asc" | "desc";

type SpaceGrant = {
  userId: string | null;
  userGroupId: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
  customRole?: {
    rolePermissions?: ReadonlyArray<{ permission: string }>;
  } | null;
};

type SpaceInput = {
  id: string;
  mode: SpaceMode;
  createdAt: number;
  defaultJoinBuiltinRoleId: string | null;
  defaultJoinCustomRoleId: string | null;
  roleGrantsAsScope: readonly SpaceGrant[];
};

export type SpaceRow = {
  id: string;
  name: string;
  mode: SpaceMode;
  discoverable: boolean;
  memberCount: number;
  memberUserIds: string[];
  isMember: boolean;
  managerUserIds: string[];
  workflowCount: number;
  chatCount: number;
  createdAt: number;
};

export function isSpacesTab(value: string | undefined): value is SpacesTab {
  return (
    value === "all" ||
    value === "member" ||
    value === "not-member" ||
    value === "personal"
  );
}

export function buildSpaceRows<S extends SpaceInput>(
  spaces: readonly S[],
  memberUserIds: ReadonlyMap<string, readonly string[]>,
  currentUserId: string | null,
  displayName: (space: S) => string,
  workflowCounts?: ReadonlyMap<string, number>,
  chatCounts?: ReadonlyMap<string, number>
): SpaceRow[] {
  return spaces.map((space) => {
    const memberIds = spaceMemberUserIds(
      space.roleGrantsAsScope,
      memberUserIds
    );
    const managerIds = new Set<string>();
    for (const grant of space.roleGrantsAsScope) {
      if (!grantHasPermission(grant, "space:manage")) continue;
      if (grant.userId) managerIds.add(grant.userId);
      if (grant.userGroupId) {
        for (const uid of memberUserIds.get(grant.userGroupId) ?? []) {
          managerIds.add(uid);
        }
      }
    }
    return {
      id: space.id,
      name: displayName(space),
      mode: space.mode,
      discoverable: Boolean(
        space.defaultJoinBuiltinRoleId || space.defaultJoinCustomRoleId
      ),
      memberCount: memberIds.size,
      memberUserIds: [...memberIds],
      isMember: currentUserId != null && memberIds.has(currentUserId),
      managerUserIds: [...managerIds],
      workflowCount: workflowCounts?.get(space.id) ?? 0,
      chatCount: chatCounts?.get(space.id) ?? 0,
      createdAt: space.createdAt,
    };
  });
}

export function filterBySearch(
  rows: readonly SpaceRow[],
  search: string
): SpaceRow[] {
  const lower = search.trim().toLowerCase();
  if (!lower) return [...rows];
  return rows.filter((r) => r.name.toLowerCase().includes(lower));
}

export function filterByManager(
  rows: readonly SpaceRow[],
  manager: string
): SpaceRow[] {
  if (manager === "all") return [...rows];
  if (manager === "none") {
    return rows.filter((r) => r.managerUserIds.length === 0);
  }
  return rows.filter((r) => r.managerUserIds.includes(manager));
}

export function filterByMember(
  rows: readonly SpaceRow[],
  member: string
): SpaceRow[] {
  if (member === "all") return [...rows];
  return rows.filter((r) => r.memberUserIds.includes(member));
}

export function filterByTab(
  rows: readonly SpaceRow[],
  tab: SpacesTab
): SpaceRow[] {
  switch (tab) {
    case "personal":
      return rows.filter((r) => r.mode === "personal");
    case "all":
      return rows.filter((r) => r.mode !== "personal");
    case "member":
      return rows.filter((r) => r.mode !== "personal" && r.isMember);
    case "not-member":
      return rows.filter((r) => r.mode !== "personal" && !r.isMember);
  }
}

export function countByTab(
  rows: readonly SpaceRow[]
): Record<SpacesTab, number> {
  let all = 0;
  let member = 0;
  let personal = 0;
  for (const row of rows) {
    if (row.mode === "personal") {
      personal++;
      continue;
    }
    all++;
    if (row.isMember) member++;
  }
  return { all, member, "not-member": all - member, personal };
}

export function sortSpaceRows(
  rows: readonly SpaceRow[],
  column: SpaceSortColumn | null,
  direction: SortDirection
): SpaceRow[] {
  const list = [...rows];
  if (!column) return list;
  list.sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;
    switch (column) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "members":
        aVal = a.memberCount;
        bVal = b.memberCount;
        break;
      case "workflows":
        aVal = a.workflowCount;
        bVal = b.workflowCount;
        break;
      case "chats":
        aVal = a.chatCount;
        bVal = b.chatCount;
        break;
      case "created":
        aVal = a.createdAt;
        bVal = b.createdAt;
        break;
    }
    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
  return list;
}
